import { z } from 'zod'
import { HASH_STRING_PATTERN } from './version.js'

/**
 * Recipient attachments — Part Three D10, register §6/§8.
 *
 * Lifetime only. A request carries its owner, label, instructions, required/optional
 * state, allowed types and counts, per-file and total limits, and explicit authorized
 * viewers. Accepted attachments are immutable response artifacts described by a
 * manifest binding file digest, size, type, owner, field ID and response ID; the
 * manifest stays separate from the signed agreement PDF.
 *
 * The numeric limits are register values (`attachment_max_files_per_field`,
 * `attachment_max_file_bytes`, `attachment_max_total_bytes`). They are applied through
 * `attachmentRequestSchema(limits)` so the register remains the single source — no C/D
 * limit appears as a literal in these contracts.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()

/** PDF/JPEG/PNG only (register §8). */
export const AttachmentFileType = z.enum(['PDF', 'JPEG', 'PNG'])
export type AttachmentFileType = z.infer<typeof AttachmentFileType>

/** Default visibility is uploader + sender; finalizer access follows review (D10). */
export const AttachmentViewer = z.enum(['UPLOADER', 'SENDER', 'FINALIZER'])
export type AttachmentViewer = z.infer<typeof AttachmentViewer>

export const AttachmentOwner = z
  .object({
    kind: z.enum(['SENDER', 'SIGNER_ROLE']),
    role_id: z.string().min(1).max(64).optional(),
  })
  .strict()
  .superRefine((owner, ctx) => {
    if (owner.kind === 'SIGNER_ROLE' && owner.role_id === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a signer-owned attachment request names its role',
        path: ['role_id'],
      })
    }
    if (owner.kind === 'SENDER' && owner.role_id !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'role_id is only valid for a signer-owned request',
        path: ['role_id'],
      })
    }
  })
export type AttachmentOwner = z.infer<typeof AttachmentOwner>

export const AttachmentRequest = z
  .object({
    id: Id,
    field_id: z.string().min(1).max(64),
    owner: AttachmentOwner,
    label: z.string().min(1),
    instructions: z.string().optional(),
    required: z.boolean(),
    allowed_types: z.array(AttachmentFileType).min(1),
    max_files: z.number().int().positive(),
    max_file_bytes: z.number().int().positive(),
    max_total_bytes: z.number().int().positive(),
    authorized_viewers: z.array(AttachmentViewer).min(1),
    /** Never folded into the signed agreement PDF (D10, D13). */
    appended_to_signed_pdf: z.literal(false),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (request.max_file_bytes > request.max_total_bytes) {
      ctx.addIssue({
        code: 'custom',
        message: 'a per-file limit cannot exceed the total limit',
        path: ['max_file_bytes'],
      })
    }
  })
export type AttachmentRequest = z.infer<typeof AttachmentRequest>

/** Register-owned attachment limits. Values come from register §8, never from here. */
export interface AttachmentLimits {
  readonly allowed_types: readonly AttachmentFileType[]
  readonly max_files_per_field: number
  readonly max_file_bytes: number
  readonly max_total_bytes: number
  readonly default_viewers: readonly AttachmentViewer[]
}

export type AttachmentLimitViolationCode =
  | 'TYPE_NOT_ALLOWED'
  | 'MAX_FILES_EXCEEDED'
  | 'MAX_FILE_BYTES_EXCEEDED'
  | 'MAX_TOTAL_BYTES_EXCEEDED'
  | 'VIEWER_NOT_PERMITTED'

export interface AttachmentLimitViolation {
  readonly code: AttachmentLimitViolationCode
  readonly limit: number | string
}

/**
 * Enforce the register's attachment limits on a request, at the boundary: a request at
 * the limit is accepted, one unit over is rejected. Attachment bytes are never
 * silently appended to the signed PDF and one managed envelope still consumes one
 * credit regardless of attachments.
 */
export function validateAttachmentRequest(
  request: AttachmentRequest,
  limits: AttachmentLimits,
): readonly AttachmentLimitViolation[] {
  const violations: AttachmentLimitViolation[] = []
  const allowedTypes = new Set<string>(limits.allowed_types)
  for (const type of request.allowed_types) {
    if (!allowedTypes.has(type)) {
      violations.push({ code: 'TYPE_NOT_ALLOWED', limit: type })
    }
  }
  if (request.max_files > limits.max_files_per_field) {
    violations.push({ code: 'MAX_FILES_EXCEEDED', limit: limits.max_files_per_field })
  }
  if (request.max_file_bytes > limits.max_file_bytes) {
    violations.push({ code: 'MAX_FILE_BYTES_EXCEEDED', limit: limits.max_file_bytes })
  }
  if (request.max_total_bytes > limits.max_total_bytes) {
    violations.push({ code: 'MAX_TOTAL_BYTES_EXCEEDED', limit: limits.max_total_bytes })
  }
  const permittedViewers = new Set<string>(limits.default_viewers)
  for (const viewer of request.authorized_viewers) {
    if (!permittedViewers.has(viewer)) {
      violations.push({ code: 'VIEWER_NOT_PERMITTED', limit: viewer })
    }
  }
  return violations
}

/**
 * A request schema whose maxima are the register's. The returned schema applies the
 * base shape plus a refinement that rejects any request above the supplied limits.
 */
export function attachmentRequestSchema(limits: AttachmentLimits) {
  return AttachmentRequest.superRefine((request, ctx) => {
    for (const violation of validateAttachmentRequest(request, limits)) {
      ctx.addIssue({
        code: 'custom',
        message: `attachment limit violated: ${violation.code}`,
        path: ['max_files'],
      })
    }
  })
}

/**
 * Immutable accepted-attachment manifest — D10/D13. Binds file digest, size, type,
 * owner, field ID and response ID; `separate_from_signed_pdf` records that attachment
 * bytes are never presented as content every party signed.
 */
export const AttachmentManifest = z
  .object({
    manifest_version: z.number().int().positive(),
    attachment_id: Id,
    request_id: Id,
    response_id: Id,
    field_id: z.string().min(1).max(64),
    owner: AttachmentOwner,
    file_digest: z.string().regex(HASH_STRING_PATTERN),
    size_bytes: z.number().int().positive(),
    file_type: AttachmentFileType,
    sanitized_file_name: z.string().min(1).max(300),
    created_at: IsoDateTime,
    separate_from_signed_pdf: z.literal(true),
  })
  .strict()
export type AttachmentManifest = z.infer<typeof AttachmentManifest>
